import React, { useEffect, useState, MouseEvent } from 'react';
import Layout from '../../components/global/layout';
import EditProfile from '../../components/Profile/Edit';
import ChangePassword from '../../components/Profile/ChangePassword';
import GroupSettings from '../../components/Profile/GroupSettings';
import TwoFactorAuth from '../../components/Profile/TwoFactorAuth';
import CmPreferences from '../CmPreferences';
import ApiClient from '../../methods/api/apiClient';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import environment from '../../environment';

const Settings: React.FC = () => {
  const user = useSelector((state: any) => state.user);
  const [tabs, setTabs] = useState<string>('profile');
  const [form, setForm] = useState<any>();
  const { tab, id: editingUserId } = useParams<{ tab?: string; id?: string }>();

  const isGroupLeader = environment.groupLeaderRoleIds.includes(
    user?.customerRole?._id || user?.customerRole
  );
  const connectMeetLabel = user?.connectMeetLabel || 'ConnectMeet';
  // console.log('DEBUG Settings:', {
  //   customerRole: user?.customerRole,
  //   customerRoleId: user?.customerRole?._id,
  //   groupLeaderRoleIds: environment.groupLeaderRoleIds,
  //   isGroupLeader
  // });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const value = {
      rescheduleTime: form?.rescheduleTime,
      id: form?.id
    };
    ApiClient.put('setting', value).then(res => {
      if (res.success) {
        // success
      }
    });
  };

  const handleTabClick =
    (tabName: string) =>
    (e: MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      setTabs(tabName);
    };

    // cw: prevent loading in Settings page
    const [groupData, setGroupData] = useState(null);

    useEffect(() => {
      if (isGroupLeader && user?.groupId) {
        const groupId = user?.groupId?._id || user?.groupId;
        ApiClient.get('api/group/detail', { id: groupId }).then(res => {
          if (res.success) setGroupData(res.data);
        });
      }
    }, [isGroupLeader, user]);

    useEffect(() => {
      if (editingUserId) {
        setTabs('profile');
      } else if (tab === 'edit') {
        setTabs('profile');
      } else if (tab === 'personal-settings') {
        setTabs('personal-settings');
      } else if (tab === 'change-password') {
        // The menu links to /profile/change-password, but the internal tab key is 'change-pass'.
        // Normalize so the Change Password form renders instead of a blank page.
        setTabs('change-pass');
      } else {
        setTabs(tab || 'profile');
      }
    }, [tab, editingUserId]);

  // When arriving via …/profile/personal-settings#cm-preferences (from the CM emails / scheduler),
  // scroll down to the ConnectMeet Preferences section once it has rendered.
  useEffect(() => {
    const hashId = window.location.hash.replace('#', '');
    const wantsCal = window.location.search.includes('calendar=');
    // Which section to scroll to: the URL hash if present, else the calendar card after an OAuth return.
    const targetId = hashId || (wantsCal ? 'calendar-connections' : '');
    if (tabs === 'personal-settings' && targetId) {
      // The CM Preferences UI + 2FA section load async and grow AFTER first paint, which pushes the
      // target down. So: poll until the element exists, scroll, then re-scroll a couple of times once
      // the late-loading content has settled so we land on the right card (not above it).
      const timers: ReturnType<typeof setTimeout>[] = [];
      let tries = 0;
      const scrollToTarget = () => {
        const el = document.getElementById(targetId);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return !!el;
      };
      const poll = setInterval(() => {
        if (scrollToTarget() || ++tries > 40) {
          clearInterval(poll);
          timers.push(setTimeout(scrollToTarget, 500));
          timers.push(setTimeout(scrollToTarget, 1200));
        }
      }, 200);
      return () => { clearInterval(poll); timers.forEach(clearTimeout); };
    }
  }, [tabs]);

  return (
    <Layout>
      {!tab && !editingUserId && (
        <div>
          <h3 className="mb-3">Settings</h3>
          <ul className="nav nav-tabs">
            <li className="nav-item">
              <a
                className={tabs === 'profile' ? 'nav-link active' : 'nav-link'}
                href="#"
                onClick={handleTabClick('profile')}
              >
                Edit Profile
              </a>
            </li>

            <li className="nav-item">
              <a
                className={tabs === 'change-pass' ? 'nav-link active' : 'nav-link'}
                href="#"
                onClick={handleTabClick('change-pass')}
              >
                Change Password
              </a>
            </li>

            {isGroupLeader && (
              <li className="nav-item">
                <a
                  className={
                    tabs === 'group-settings'
                      ? 'nav-link active'
                      : 'nav-link'
                  }
                  href="#"
                  onClick={handleTabClick('group-settings')}
                >
                  Group Settings
                </a>
              </li>
            )}

            <li className="nav-item">
              <a
                className={tabs === 'security' ? 'nav-link active' : 'nav-link'}
                href="#"
                onClick={handleTabClick('security')}
              >
                Security
              </a>
            </li>
          </ul>
        </div>
      )}

      <div>
        {tabs === 'profile' && <EditProfile />}
        {tabs === 'change-pass' && <ChangePassword />}
        {tabs === 'group-settings' && isGroupLeader && <GroupSettings initialData={groupData} />}
        {tabs === 'security' && <TwoFactorAuth />}
        {tabs === 'personal-settings' && (
          <div>
            <h3 className="mb-4">Personal Settings</h3>
            <div className="mb-6">
              <h4 className="text-base font-semibold mb-2">Change Password</h4>
              <ChangePassword hideTitle />
            </div>
            <div>
              <h4 className="text-base font-semibold mb-2">Security</h4>
              <TwoFactorAuth />
            </div>
            <div id="cm-preferences" className="mt-6 scroll-mt-20">
              <h4 className="text-base font-semibold mb-2">{connectMeetLabel} Preferences</h4>
              <p className="text-sm text-gray-500 mb-3">
                Set your favorite places, maximum driving distance, preferred video link, and connect your Google/Outlook calendar for {connectMeetLabel} scheduling.
              </p>
              <CmPreferences embedded />
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Settings;
